import type { TranscriptSegmentData } from './parse-transcript';
import type { SpeakerLabel } from './types/types';

function nameFromEmail(email: string | null | undefined): string | null {
  if (!email) return null;
  const at = email.indexOf('@');
  if (at <= 0) return null;
  return email.slice(0, at).trim() || null;
}

/**
 * Resolves a segment's display speaker name, applying the recording's editable
 * speaker-label overrides (keyed by `speaker_id`) and — when provided — the live
 * attendee name for the labeled email. Used by the avatar, chips and inline
 * label so all three stay in sync.
 *
 * Resolution order:
 *   1. attendee display name for the label's email (live; case-insensitive)
 *   2. label.name (legacy stored name)
 *   3. when `attendeeNameByEmail` is provided, the labeled email is treated as
 *      attendee-driven — if it's no longer an attendee, we REVERT to the raw
 *      speaker rather than show the email local-part. When the lookup is omitted
 *      (legacy callers), fall back to the email local-part for back-compat.
 *   4. raw transcript speaker → "Speaker {id}" → "Unknown"
 */
export function resolveSpeakerName(
  segment: Pick<TranscriptSegmentData, 'speaker' | 'speaker_id'>,
  speakerLabels: Record<string, SpeakerLabel>,
  attendeeNameByEmail?: Record<string, string>,
): string {
  const label = segment.speaker_id != null ? speakerLabels[String(segment.speaker_id)] : undefined;

  if (label?.email && attendeeNameByEmail) {
    // Attendee-driven: the name comes ONLY from the live attendee list. On a
    // miss (attendee removed) we revert to the raw speaker — we deliberately do
    // NOT fall back to the stored label.name, which is a stale snapshot from a
    // previous edit and would keep showing the removed person.
    const match = attendeeNameByEmail[label.email.toLowerCase()];
    if (match) return match;
  } else if (label) {
    // Legacy path (no live attendee lookup): email local-part, then stored name.
    const legacy = nameFromEmail(label.email) || label.name || null;
    if (legacy) return legacy;
  }

  if (segment.speaker) return segment.speaker;
  if (segment.speaker_id != null) return `Speaker ${segment.speaker_id}`;
  return 'Unknown';
}
